import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface RouteSEOConfig {
  title: string;
  description: string;
}

const ROUTE_SEO: Record<string, RouteSEOConfig> = {
  '/': {
    title: 'Flamingo — AI Rental Platform for Renters & Landlords',
    description: 'Trust-based renting with AI matchmaking, verified listings, and secure chat. Swipe, match, and message on Flamingo.'
  },
  '/dashboard': {
    title: 'Renter Dashboard — Personalized Matches | Flamingo',
    description: 'See your rental matches, messages, and next steps. Smart insights tailored to your preferences.'
  },
  '/discover': {
    title: 'Discover Rentals — Swipe to Match | Flamingo',
    description: 'Swipe through verified rentals ranked by your match %. Like, pass, and message landlords securely.'
  },
  '/matches': {
    title: 'Your Matches — Best-Fit Rentals | Flamingo',
    description: 'Review and act on your top rental matches with clear next steps and secure messaging.'
  },
  '/messages': {
    title: 'Messages — Secure Renter–Landlord Chat | Flamingo',
    description: 'Chat securely with verified landlords. Get answers fast with AI-assisted messaging.'
  },
  '/landlord-dashboard': {
    title: 'Landlord Dashboard — Manage Listings | Flamingo',
    description: 'Track listings, leads, and performance. Manage tenants and tours in one place.'
  },
  '/landlord-analytics': {
    title: 'Landlord Analytics — Views, Likes, Applications | Flamingo',
    description: 'Actionable portfolio analytics: views, inquiries, applications, and occupancy trends.'
  },
  '/landlord-messages': {
    title: 'Landlord Messages — Respond to Leads | Flamingo',
    description: 'Respond quickly to renter inquiries. Keep conversations organized and actionable.'
  },
  '/landlord-leases': {
    title: 'Landlord Leases — Generate & Track | Flamingo',
    description: 'Create, manage, and track leases with clarity. Streamlined and secure for both parties.'
  },
  '/landlord-tours': {
    title: 'Landlord Tours — Schedule & Confirm | Flamingo',
    description: 'Manage tour requests, confirmations, and outcomes. Keep your pipeline moving.'
  },
  '/add-listing': {
    title: 'Add Listing — Syndicate Your Rental | Flamingo',
    description: 'Create a verified listing with photos, amenities, and availability in minutes.'
  },
  '/pricing': {
    title: 'Pricing — Free for Renters, Pro for Landlords | Flamingo',
    description: 'Simple pricing. Free for renters. Powerful plans for landlords and property teams.'
  },
  '/help': {
    title: 'Help Center — Support & Guides | Flamingo',
    description: 'Get help with finding properties, messaging, account management, billing, safety, and more.'
  },
  '/help/finding-properties': {
    title: 'Finding Properties — Search & Match Guide | Flamingo',
    description: 'Learn how to discover, filter, and save rental properties using AI-powered matchmaking and swipe features.'
  },
  '/help/communication': {
    title: 'Communication — Messaging Guide | Flamingo',
    description: 'Best practices for messaging landlords, staying safe, and getting quick responses on Flamingo.'
  },
  '/help/landlords': {
    title: 'For Landlords — Property Management Guide | Flamingo',
    description: 'Complete guide to listing properties, managing tenants, and growing your rental business on Flamingo.'
  },
  '/help/account': {
    title: 'Account Management — Settings & Privacy | Flamingo',
    description: 'Manage your profile, update preferences, control privacy settings, and secure your Flamingo account.'
  },
  '/help/billing': {
    title: 'Billing & Pricing — Subscriptions & Payments | Flamingo',
    description: 'Everything about Flamingo pricing, payment methods, subscriptions, refunds, and billing support.'
  },
  '/help/safety': {
    title: 'Safety & Security — Stay Safe While Renting | Flamingo',
    description: 'Learn how to protect yourself from scams, verify listings, and use Flamingo safely as a renter or landlord.'
  }
};

function setOrCreateMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    const tag = selector.startsWith('meta[') ? 'meta' : selector.startsWith('link[') ? 'link' : 'meta';
    el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el!);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  }
}

export const useRouteSEO = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname as keyof typeof ROUTE_SEO;
    const config: RouteSEOConfig | undefined = ROUTE_SEO[path] || ROUTE_SEO['/'];
    const title = config.title;
    const description = config.description;
    const canonical = `${window.location.origin}${location.pathname}`;

    // Title
    document.title = title;

    // Meta description
    setOrCreateMeta('meta[name="description"]', { name: 'description', content: description });

    // Canonical
    setOrCreateMeta('link[rel="canonical"]', { rel: 'canonical', href: canonical });

    // Open Graph
    setOrCreateMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    setOrCreateMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    setOrCreateMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });

    // Twitter
    setOrCreateMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setOrCreateMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  }, [location.pathname, location.search, location.hash]);
};
